import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HotelIcon from '@mui/icons-material/Hotel';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import useHotelList from "../hooks/useHotelList";
import useCustomToast from "../hooks/useCustomToast";
import axios from 'axios';
import {
  hasAddPropertyDraftToResume,
  loadSavedStep,
  getFirstStep,
  getTotalSteps,
  draftStore,
} from '../pages/listing/onboardingDraft';
import {
  PROPERTY_STATUS,
  PROPERTY_STATUS_ORDER,
  classifyPropertyStatus,
  getPropertyStatusMeta,
} from './propertyStatus';

const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemAnimation = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const StatusChip = ({ statusKey, extraLabel }) => {
  const meta = getPropertyStatusMeta(statusKey);
  return (
    <Chip
      label={extraLabel || meta.label}
      size="small"
      sx={{
        backgroundColor: meta.bg,
        color: meta.color,
        height: '24px',
        fontWeight: 700,
        fontSize: '0.75rem',
      }}
    />
  );
};

const MyProperties = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { successToast } = useCustomToast();
  const { hotels, loading } = useHotelList();
  const [selectedHotelId, setSelectedHotelId] = useState(localStorage.getItem("selectedHotelId"));
  const [searchQuery, setSearchQuery] = useState('');
  const [draftProperty, setDraftProperty] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const storedHotelId = localStorage.getItem("selectedHotelId");
    setSelectedHotelId(storedHotelId);

    const handleStorageChange = (e) => {
      if (e.key === "selectedHotelId") {
        setSelectedHotelId(e.newValue);
      }
    };

    const handleHotelSelected = (e) => {
      if (e.detail && e.detail.hotelId) {
        setSelectedHotelId(e.detail.hotelId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('hotelSelected', handleHotelSelected);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('hotelSelected', handleHotelSelected);
    };
  }, []);

  useEffect(() => {
    if (hotels && hotels.length > 0) {
      const firstHotel = hotels[0];
      const token = localStorage.getItem('token');
      if (firstHotel && token) {
        const fetchHotelDetails = async () => {
          try {
            const formData = new FormData();
            formData.append('hotel_id', firstHotel.hotel_id);
            formData.append('token', token);
            const response = await axios.post('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails', formData);
            if (response.data && response.data.status) {
              localStorage.setItem('firstHotelDetails', JSON.stringify(response.data));
            }
          } catch (err) {
            // Optionally handle error
          }
        };
        fetchHotelDetails();
      }
    }
  }, [hotels]);

  useEffect(() => {
    const computeDraft = () => {
      const draftActive = hasAddPropertyDraftToResume();
      if (draftActive) {
        const step = loadSavedStep('add-property');
        const name = draftStore.getItem('propertyName') || '';
        const type = draftStore.getItem('selectedPropertyType') || '';
        const city = draftStore.getItem('city') || '';
        const state = draftStore.getItem('state') || '';
        const address = [draftStore.getItem('address'), city, state].filter(Boolean).join(', ');
        const stepNum = step != null ? step - getFirstStep('add-property') + 1 : 0;
        const total = getTotalSteps('add-property');
        setDraftProperty({
          draft: true,
          hotel_id: 'draft',
          hotel_name: name || 'Untitled Property',
          type,
          address: address || 'Location not set yet',
          step: stepNum,
          totalSteps: total,
        });
      } else {
        setDraftProperty(null);
      }
    };

    computeDraft();

    const handleDraftEvent = () => computeDraft();
    window.addEventListener('storage', handleDraftEvent);
    window.addEventListener('listingDraftChanged', handleDraftEvent);
    window.addEventListener('focus', handleDraftEvent);

    return () => {
      window.removeEventListener('storage', handleDraftEvent);
      window.removeEventListener('listingDraftChanged', handleDraftEvent);
      window.removeEventListener('focus', handleDraftEvent);
    };
  }, []);

  const getFirstImage = (hotelImages) => {
    try {
      const images = JSON.parse(hotelImages);
      for (const roomType in images) {
        if (images[roomType] && images[roomType].length > 0) {
          return `https://api.tabtrips.com/olivrweb/hotelapi/${images[roomType][0]}`;
        }
      }
    } catch (e) {
      console.error('Error parsing hotel images:', e);
    }
    return 'https://via.placeholder.com/300x200?text=No+Image+Available';
  };

  const handlePropertySelect = (hotel) => {
    localStorage.setItem("selectedHotelId", hotel.hotel_id);
    localStorage.setItem("selectedHotelName", hotel.hotel_name);
    localStorage.setItem("selectedHostId", hotel.host_id);
    setSelectedHotelId(hotel.hotel_id);

    window.dispatchEvent(new CustomEvent('hotelSelected', {
      detail: {
        hotelId: hotel.hotel_id,
        hotelName: hotel.hotel_name,
        hotel: hotel
      }
    }));

    window.dispatchEvent(new StorageEvent('storage', {
      key: 'selectedHotelId',
      newValue: hotel.hotel_id,
      oldValue: localStorage.getItem('selectedHotelId')
    }));

    successToast(`Selected ${hotel.hotel_name}`);
    navigate('/dashboard');
  };

  const handleRatesCalendar = (e, hotelId) => {
    e.stopPropagation();
    navigate(`/room-rates-calendar/${hotelId}`);
  };

  const catalog = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matchesQuery = (item) => {
      if (!query) return true;
      return (
        item.hotel_name?.toLowerCase().includes(query) ||
        item.address?.toLowerCase().includes(query)
      );
    };

    const items = [];
    if (draftProperty && matchesQuery(draftProperty)) items.push(draftProperty);
    (hotels || []).forEach((hotel) => {
      if (matchesQuery(hotel)) items.push(hotel);
    });

    const grouped = {
      active: [],
      review: [],
      inactive: [],
      draft: [],
    };
    items.forEach((item) => {
      const key = classifyPropertyStatus(item);
      grouped[key].push(item);
    });

    return {
      items,
      grouped,
      counts: {
        all: items.length,
        active: grouped.active.length,
        review: grouped.review.length,
        inactive: grouped.inactive.length,
        draft: grouped.draft.length,
      },
    };
  }, [hotels, draftProperty, searchQuery]);

  const visibleSections = statusFilter === 'all'
    ? PROPERTY_STATUS_ORDER.filter((key) => catalog.grouped[key].length > 0)
    : [statusFilter];

  const renderDraftRow = (item) => (
    <ListItem
      key="draft-property"
      onClick={() => navigate('/add-property')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        cursor: 'pointer',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        transition: 'all 0.3s ease',
        backgroundColor: 'rgba(255,119,0,0.03)',
        borderLeft: '4px solid #FF7700',
        '&:hover': {
          backgroundColor: 'rgba(255,119,0,0.06)',
        },
        '&:last-child': { borderBottom: 'none' },
      }}
      component={motion.div}
      variants={itemAnimation}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
        <EditNoteIcon sx={{ color: '#FF7700', fontSize: '1.5rem' }} />
      </Box>

      <Avatar
        variant="rounded"
        sx={{
          width: 80,
          height: 80,
          borderRadius: 2,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditNoteIcon sx={{ color: '#fff', fontSize: '2rem' }} />
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2E4057',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              mb: 0.5,
            }}
          >
            {item.hotel_name}
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={(e) => { e.stopPropagation(); navigate('/add-property'); }}
            sx={{
              background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
              color: '#fff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 0.75,
              px: 2,
              flexShrink: 0,
              '&:hover': {
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                boxShadow: '0 2px 10px rgba(255, 119, 0, 0.3)',
              },
            }}
          >
            Continue
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocationOnIcon sx={{ color: '#FF7700', fontSize: '1rem' }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.address}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip statusKey="draft" />
          {item.step > 0 && (
            <Chip
              label={`Step ${item.step} of ${item.totalSteps}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(100, 116, 139, 0.1)',
                color: '#64748b',
                height: '24px',
              }}
            />
          )}
        </Box>
      </Box>
    </ListItem>
  );

  const renderHotelRow = (hotel, statusKey) => {
    const selected = String(selectedHotelId) === String(hotel.hotel_id);
    const muted = statusKey === 'inactive';
    return (
      <ListItem
        key={hotel.hotel_id}
        onClick={() => handlePropertySelect(hotel)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease',
          opacity: muted ? 0.78 : 1,
          backgroundColor: selected ? 'rgba(255,119,0,0.05)' : 'transparent',
          borderLeft: selected ? '4px solid #FF7700' : '4px solid transparent',
          '&:hover': {
            backgroundColor: selected ? 'rgba(255,119,0,0.08)' : 'rgba(255,119,0,0.02)',
          },
          '&:last-child': { borderBottom: 'none' },
        }}
        component={motion.div}
        variants={itemAnimation}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          {selected ? (
            <CheckCircleIcon sx={{ color: '#FF7700', fontSize: '1.5rem' }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: '1.5rem' }} />
          )}
        </Box>

        <Avatar
          variant="rounded"
          src={getFirstImage(hotel.hotel_images)}
          alt={hotel.hotel_name}
          sx={{
            width: 80,
            height: 80,
            borderRadius: 2,
            flexShrink: 0,
            filter: muted ? 'grayscale(0.35)' : 'none',
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#2E4057',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                mb: 0.5,
              }}
            >
              {hotel.hotel_name}
            </Typography>

            <Tooltip title="View Room Rates Calendar">
              <IconButton
                onClick={(e) => handleRatesCalendar(e, hotel.hotel_id)}
                size="small"
                sx={{
                  color: '#FF7700',
                  '&:hover': { backgroundColor: 'rgba(255,119,0,0.1)' },
                }}
              >
                <HotelIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <LocationOnIcon sx={{ color: '#FF7700', fontSize: '1rem' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hotel.address}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`Property ID: ${hotel.hotel_id}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(46, 64, 87, 0.08)',
                color: '#2E4057',
                height: '24px',
                fontWeight: 700,
              }}
            />
            <StatusChip statusKey={statusKey} />
            <Chip
              icon={<StarIcon sx={{ fontSize: '0.875rem !important' }} />}
              label={`${hotel.rating || '—'} Rating`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 184, 0, 0.1)',
                color: '#FFB800',
                height: '24px',
                '& .MuiChip-icon': { color: '#FFB800' }
              }}
            />
            <Chip
              icon={<HotelIcon sx={{ fontSize: '0.875rem !important' }} />}
              label={hotel.type || 'Hotel'}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 119, 0, 0.1)',
                color: '#FF7700',
                height: '24px',
                '& .MuiChip-icon': { color: '#FF7700' }
              }}
            />
          </Box>
        </Box>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress sx={{ color: '#FF7700' }} />
      </Box>
    );
  }

  const hasAnyProperty = (hotels && hotels.length > 0) || !!draftProperty;
  const showSearch = ((hotels?.length || 0) + (draftProperty ? 1 : 0)) > 1;

  return (
    <Box
      component={motion.div}
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      sx={{
        maxWidth: '1400px',
        margin: '0 auto',
        p: { xs: 2, sm: 3 },
      }}
    >
      <motion.div variants={itemAnimation}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 2, md: 3 }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#2E4057',
                fontSize: { xs: '1.5rem', sm: '2rem' },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: 0,
                  width: '60px',
                  height: '4px',
                  background: 'linear-gradient(90deg, #FF7700, transparent)',
                  borderRadius: '2px',
                }
              }}
            >
              My Properties
            </Typography>

            {showSearch && (
              <TextField
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  minWidth: { xs: '100%', sm: '200px', md: '250px' },
                  maxWidth: { xs: '100%', sm: '300px', md: '350px' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    height: { xs: '40px', sm: '42px' },
                    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,119,0,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#FF7700' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#FF7700', fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {hasAnyProperty && (
            <Button
              variant="contained"
              startIcon={<AddBusinessIcon />}
              onClick={() => navigate('/add-property')}
              sx={{
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 2.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  boxShadow: '0 4px 15px rgba(255, 119, 0, 0.3)',
                },
              }}
            >
              Add Property
            </Button>
          )}
        </Box>
      </motion.div>

      {hasAnyProperty && (
        <motion.div variants={itemAnimation}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2.5,
              flexWrap: 'wrap',
            }}
          >
            {[{ key: 'all', label: 'All' }, ...PROPERTY_STATUS_ORDER.map((key) => PROPERTY_STATUS[key])].map((tab) => {
              const selected = statusFilter === tab.key;
              const count = catalog.counts[tab.key] || 0;
              const color = tab.color || '#FF7700';
              return (
                <Chip
                  key={tab.key}
                  clickable
                  onClick={() => setStatusFilter(tab.key)}
                  label={`${tab.label}  ${count}`}
                  sx={{
                    height: 34,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    borderRadius: '999px',
                    backgroundColor: selected ? color : '#fff',
                    color: selected ? '#fff' : '#334155',
                    border: selected ? 'none' : '1px solid rgba(15, 23, 42, 0.12)',
                    boxShadow: selected ? '0 6px 16px rgba(15, 23, 42, 0.12)' : 'none',
                    '&:hover': {
                      backgroundColor: selected ? color : 'rgba(255,119,0,0.06)',
                    },
                  }}
                />
              );
            })}
          </Box>
        </motion.div>
      )}

      {hasAnyProperty && (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          {catalog.items.length === 0 && (
            <Paper elevation={0} sx={{ borderRadius: '16px', p: 4, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="body1" color="text.secondary">
                No properties found matching "{searchQuery}"
              </Typography>
            </Paper>
          )}

          {catalog.items.length > 0 && visibleSections.map((sectionKey) => {
            const meta = PROPERTY_STATUS[sectionKey];
            const rows = catalog.grouped[sectionKey];
            const SectionIcon = sectionKey === 'active'
              ? CheckCircleIcon
              : sectionKey === 'review'
                ? HourglassEmptyIcon
                : sectionKey === 'inactive'
                  ? PauseCircleOutlineIcon
                  : EditNoteIcon;

            return (
              <Paper
                key={sectionKey}
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    background: `linear-gradient(90deg, ${meta.bg} 0%, #fff 72%)`,
                    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        backgroundColor: '#fff',
                        color: meta.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: `1px solid ${meta.bg}`,
                      }}
                    >
                      <SectionIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.98rem', lineHeight: 1.2 }}>
                        {meta.label}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                        {meta.hint}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={rows.length}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      backgroundColor: meta.bg,
                      color: meta.color,
                    }}
                  />
                </Box>

                {rows.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No properties in {meta.label}{searchQuery.trim() ? ' match this search' : ''}.
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {rows.map((item) => (
                      item.draft ? renderDraftRow(item) : renderHotelRow(item, sectionKey)
                    ))}
                  </List>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {!loading && !hasAnyProperty && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            backgroundColor: 'rgba(255,119,0,0.02)',
            borderRadius: '16px',
            border: '2px dashed rgba(255,119,0,0.2)',
            mt: 2
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Properties Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first property
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => navigate('/add-property')}
            sx={{
              background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
              color: '#fff',
              py: 1.5,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                boxShadow: '0 4px 15px rgba(255, 119, 0, 0.3)',
              },
            }}
          >
            Add Property
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MyProperties;
